import {Injectable, OnDestroy} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MessageBusService} from '../message-bus.service';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {
    GetSkewSentiment,
    GetSkewSentimentDebug,
    GetSkewSentimentDebugReply,
    GetSkewSentimentReply,
    SkewSentimentChangedDto
} from './skew-sentiment-cqrs.model';

@Injectable()
export class SkewSentimentService implements OnDestroy {

    // ── Indicator state ──
    value: number | null = null;
    errorMessage: string | null = null;
    tier: string | null = null;
    method: string | null = null;
    chosenExpiration: string | null = null;
    timestamp: Date | null = null;

    // ── Debug panel state (populated from the same push; always in sync with indicator) ──
    debug: GetSkewSentimentDebugReply | null = null;

    // ── Notification (component subscribes for change detection) ──
    updated$ = new Subject<void>();

    private readonly _unsubscriber = new Subject<void>();

    constructor(
        private readonly _shellClient: ShellClientService,
        private readonly _messageBus: MessageBusService
    ) {
    }

    init() {
        this.fetchCurrent();

        this._messageBus.of<SkewSentimentChangedDto>('SkewSentimentChangedDto')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(msg => {
                this.applyPush(msg.payload);
                this.updated$.next();
            });
    }

    dispose() {
        this._unsubscriber.next();
        this._unsubscriber.complete();
        this.value = null;
        this.errorMessage = null;
        this.tier = null;
        this.method = null;
        this.chosenExpiration = null;
        this.timestamp = null;
        this.debug = null;
        this.updated$.next();
    }

    ngOnDestroy() {
        this.dispose();
    }

    async backfillDebugIfMissing(): Promise<void> {
        if (this.debug != null) { return; }
        try {
            const reply = await this._shellClient
                .processQuery<GetSkewSentimentDebugReply>(new GetSkewSentimentDebug());
            if (this.debug == null) {
                this.debug = reply;
                this.updated$.next();
            }
        } catch {
            // No-op — next push will populate it.
        }
    }

    private applyPush(payload: SkewSentimentChangedDto) {
        this.value = payload.hasValue ? payload.value : null;
        this.tier = payload.tier || null;
        this.method = payload.method || null;
        this.chosenExpiration = payload.chosenExpiration || null;
        this.errorMessage = payload.errorMessage || null;
        this.timestamp = payload.timestamp ? new Date(payload.timestamp) : null;

        this.debug = {
            value: payload.hasValue ? payload.value : null,
            tier: payload.tier,
            method: payload.method,
            chosenExpiration: payload.chosenExpiration,
            errorMessage: payload.errorMessage,
            atm: payload.atm,
            spxPrice: payload.spxPrice,
            spreadWidth: payload.spreadWidth,
            targetPrice: payload.targetPrice,
            majorityValue: payload.majorityValue,
            majorityCount: payload.majorityCount,
            totalProducing: payload.totalProducing,
            averageOfProducing: payload.averageOfProducing,
            excludedOutliers: payload.excludedOutliers || [],
            expirations: payload.expirations || [],
            pendingValue: payload.pendingValue,
            pendingIsNA: payload.pendingIsNA,
            pendingConfirmCount: payload.pendingConfirmCount,
            confirmThreshold: payload.confirmThreshold,
            resolvedByAnchor: payload.resolvedByAnchor,
            anchorExpiration: payload.anchorExpiration,
            anchorSupporters: payload.anchorSupporters || [],
            anchorDeniers: payload.anchorDeniers || []
        };
    }

    private async fetchCurrent() {
        try {
            const reply = await this._shellClient
                .processQuery<GetSkewSentimentReply>(new GetSkewSentiment());

            this.value = reply.value != null ? reply.value : null;
            this.tier = reply.tier || null;
            this.method = reply.method || null;
            this.chosenExpiration = reply.chosenExpiration || null;
            this.errorMessage = reply.errorMessage || null;
            this.timestamp = reply.timestamp ? new Date(reply.timestamp as any) : null;
            this.updated$.next();
        } catch {
            // Initial fetch failed — push updates will provide data.
        }
    }
}
